/**
 * Settings API — tenant settings (admin), user settings (per-user), secrets (admin).
 */
import { api } from "./client";

// ============================================================================
// Personal user settings
// ============================================================================
export interface UserSettings {
  user_id: string;
  theme: "light" | "dark" | "auto";
  locale: "vi" | "en";
  default_llm_provider: string | null;
  default_llm_model: string | null;
  notification_prefs: Record<string, unknown>;
  updated_at: string;
}

export interface UserSettingsUpdate {
  theme?: "light" | "dark" | "auto";
  locale?: "vi" | "en";
  default_llm_provider?: string | null;
  default_llm_model?: string | null;
  notification_prefs?: Record<string, unknown>;
}

// ============================================================================
// Tenant settings
// ============================================================================
export interface TenantSettings {
  id: string;
  tenant_id: string;
  default_llm_provider: string | null;
  default_llm_model: string | null;
  cost_budget_per_user_daily_usd: string | null;
  cost_budget_per_conversation_usd: string | null;
  feature_flags: Record<string, unknown>;
  masking_rules: Record<string, unknown>;
  updated_at: string;
}

export interface TenantSettingsUpdate {
  default_llm_provider?: string | null;
  default_llm_model?: string | null;
  cost_budget_per_user_daily_usd?: number | string | null;
  cost_budget_per_conversation_usd?: number | string | null;
  feature_flags?: Record<string, unknown>;
  masking_rules?: Record<string, unknown>;
}

// ============================================================================
// Secrets
// ============================================================================
export interface SecretMeta {
  name: string;
  label: string;
  format_hint: string | null;
  is_set: boolean;
  masked_value: string | null;
  created_at: string | null;
  updated_at: string | null;
  last_used_at: string | null;
  use_count: number;
}

// ============================================================================
// Profile (Doc 48)
// ============================================================================
export interface MeProfile {
  user_id: string;
  email: string;
  display_name: string | null;
  has_password: boolean;
  has_odoo_identity: boolean;
  odoo_user_id: number | null;
  odoo_db: string | null;
  last_login_at: string | null;
}

// ============================================================================
// Password
// ============================================================================
export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface SetInitialPasswordRequest {
  odoo_password: string;
  new_password: string;
}

export const settingsApi = {
  // ----- Personal -----
  getMine: () => api.get<UserSettings>("/api/v1/me/settings"),
  updateMine: (body: UserSettingsUpdate) =>
    api.patch<UserSettings>("/api/v1/me/settings", body),

  // ----- Profile -----
  getProfile: () => api.get<MeProfile>("/api/v1/me/profile"),
  updateProfile: (body: { display_name?: string | null }) =>
    api.patch<MeProfile>("/api/v1/me/profile", body),

  // ----- Password -----
  changePassword: (body: ChangePasswordRequest) =>
    api.post<{ status: string; mode?: string; message: string }>(
      "/api/v1/me/change-password",
      body,
    ),
  setInitialPassword: (body: SetInitialPasswordRequest) =>
    api.post<{ status: string; message: string }>(
      "/api/v1/me/set-password",
      body,
    ),
  /** Doc 55: self-deactivate account (soft delete). */
  deactivateAccount: (password: string) =>
    api.post<{ status: string; message: string }>(
      "/api/v1/me/account/deactivate",
      { password, confirm: true },
    ),

  // ----- Tenant (admin) -----
  getTenant: () => api.get<TenantSettings>("/api/v1/admin/tenant/settings"),
  updateTenant: (body: TenantSettingsUpdate) =>
    api.patch<TenantSettings>("/api/v1/admin/tenant/settings", body),

  // ----- Secrets (admin) -----
  listSecrets: () =>
    api.get<{ items: SecretMeta[] }>("/api/v1/admin/secrets"),
  setSecret: (name: string, value: string) =>
    api.put<{ name: string; is_set: boolean }>(
      `/api/v1/admin/secrets/${encodeURIComponent(name)}`,
      { value },
    ),
  deleteSecret: (name: string) =>
    api.delete<void>(`/api/v1/admin/secrets/${encodeURIComponent(name)}`),

  /** P3-B fix: test Odoo connection bằng credentials trong vault. */
  testOdoo: () =>
    api.post<{
      ok: boolean;
      message: string;
      detail?: Record<string, unknown> | null;
    }>("/api/v1/admin/secrets/_test/odoo"),
};
